import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight, LucideIcon, Sparkles, Music, Play, Pause, Flame, Ticket, Tag, MapPin, Users, Heart } from "lucide-react";
import AuthorChip, { type AuthorLike } from "@/components/platform/AuthorChip";
import FollowButton from "@/components/platform/FollowButton";
import { SampleBadge } from "@/components/platform/ShowcaseNotice";
import { artGradient, hueFromId } from "@/lib/showcaseData";
import { useMediaPlayer } from "@/hooks/platform/useMediaPlayer";
import { toast } from "sonner";
import MediaImage from "@/components/platform/MediaImage";

export interface DetailAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: "default" | "outline" | "secondary" | "ghost";
  disabled?: boolean;
  accent?: string;
}

export interface DetailFact {
  label: string;
  value: string | number;
  icon?: LucideIcon;
}

export interface DetailRelated {
  id: string;
  title: string;
  meta?: string;
  image?: string | null;
  hue?: number;
}

export interface DetailItem {
  id: string;
  title: string;
  kindLabel?: string;
  images?: (string | null | undefined)[];
  hue?: number;
  icon: LucideIcon;
  author?: AuthorLike | null;
  description?: string | null;
  price?: number | null;
  priceNote?: string;
  tags?: string[];
  facts?: DetailFact[];
  sample?: boolean;
  followTargetId?: string | null;
  relatedTitle?: string;
  related?: DetailRelated[];
  extra?: React.ReactNode;
}

interface Props {
  item: DetailItem | null;
  actions?: DetailAction[];
  onClose: () => void;
  onAuthorClick?: (author: AuthorLike) => void;
}

const WAVEFORM_BARS = [35, 70, 95, 50, 80, 40, 90, 65, 45, 85, 60, 75, 100, 50, 70, 80];

/** Custom Music-Culture Item & Post Discovery Dialog */
const ItemDetailDialog = ({ item, actions = [], onClose, onAuthorClick }: Props) => {
  const [index, setIndex] = useState(0);
  const { play, toggle, isPlaying, isCurrent } = useMediaPlayer();

  useEffect(() => setIndex(0), [item?.id]);

  if (!item) return null;

  const images = (item.images ?? []).filter(Boolean) as string[];
  const baseHue = item.hue ?? hueFromId(item.id);
  const frames: { url?: string; hue: number }[] = images.length
    ? images.map((url) => ({ url, hue: baseHue }))
    : [0, 40, 90].map((offset) => ({ hue: (baseHue + offset) % 360 }));
  const current = frames[Math.min(index, frames.length - 1)];
  const Icon = item.icon;

  const trackId = `item-preview-${item.id}`;
  const isAudioPlaying = isPlaying && isCurrent(trackId);

  const toggleSound = () => {
    if (isCurrent(trackId)) {
      toggle();
    } else {
      play({
        id: trackId,
        title: item.title,
        artist: item.author?.name ?? "Metro City Groove",
        audioUrl: "https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3?filename=electronic-future-beats-117997.mp3",
      });
      toast.success(`Playing audio: ${item.title}`);
    }
  };

  const isEvent = item.kindLabel === "event" || item.icon === Ticket;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800/80 text-white max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar p-0 rounded-2xl shadow-2xl shadow-black/90">
        
        {/* Holographic Iridescent Sheen Top Accent */}
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-purple-500 via-pink-500 via-amber-400 to-cyan-400 z-30 pointer-events-none animate-pulse" />

        {/* ── 1. HERO MEDIA / SOUND STAGE ── */}
        <div className="relative select-none group">
          {current.url ? (
            <MediaImage src={current.url} alt={item.title} className="h-72 sm:h-84 w-full object-cover" />
          ) : (
            <div className="relative h-72 sm:h-84 w-full" style={{ backgroundImage: artGradient(current.hue) }}>
              <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: "repeating-linear-gradient(115deg, rgba(255,255,255,.35) 0 2px, transparent 2px 12px)" }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon className="w-16 h-16 text-white/90 drop-shadow-2xl animate-pulse" />
              </div>
            </div>
          )}

          {/* Vignette bottom fade */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />

          {/* Top VIP Pass Stamp & Kind Badge */}
          <div className="absolute top-3.5 left-4 z-20 flex items-center gap-2">
            {item.kindLabel && (
              <Badge className="bg-black/75 text-white border-white/15 capitalize backdrop-blur-md shadow-xl text-xs font-mono px-2.5 py-1">
                <Sparkles className="w-3 h-3 mr-1 text-amber-400" />
                {item.kindLabel}
              </Badge>
            )}
            {item.sample && <SampleBadge />}
          </div>

          {/* Floating Waveform Audio Teaser Button */}
          <div className="absolute bottom-4 right-4 z-20 flex items-center gap-2.5 bg-black/80 backdrop-blur-md border border-white/15 rounded-2xl px-3 py-1.5 shadow-2xl">
            <button
              type="button"
              onClick={toggleSound}
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
                isAudioPlaying ? "bg-purple-600 text-white scale-105" : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              {isAudioPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
            </button>
            <div className="flex items-end gap-0.5 h-3.5 cursor-pointer" onClick={toggleSound}>
              {WAVEFORM_BARS.map((h, i) => (
                <div
                  key={i}
                  className={`w-1 rounded-full transition-all duration-300 ${
                    isAudioPlaying ? "bg-gradient-to-t from-purple-400 to-pink-400" : "bg-zinc-600"
                  }`}
                  style={{ height: isAudioPlaying ? `${h}%` : `${Math.max(25, h * 0.4)}%` }}
                />
              ))}
            </div>
          </div>

          {/* Image carousel navigation arrows */}
          {frames.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={() => setIndex((i) => (i - 1 + frames.length) % frames.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/70 border border-white/10 p-2.5 text-white backdrop-blur-sm hover:bg-black/90 transition-colors shadow-xl"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={() => setIndex((i) => (i + 1) % frames.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/70 border border-white/10 p-2.5 text-white backdrop-blur-sm hover:bg-black/90 transition-colors shadow-xl"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Frame count badge */}
          {frames.length > 1 && (
            <div className="absolute top-3.5 right-4 bg-black/70 border border-white/15 text-xs text-white px-2.5 py-0.5 rounded-full backdrop-blur-md">
              {index + 1} / {frames.length}
            </div>
          )}
        </div>

        {/* Thumbnail Strip */}
        {frames.length > 1 && (
          <div className="flex gap-2 px-5 -mt-10 relative z-10 pb-1">
            {frames.map((f, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Image ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-14 w-14 shrink-0 overflow-hidden rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                  i === index ? "border-purple-500 shadow-lg shadow-purple-900/40" : "border-white/10 hover:border-white/30"
                }`}
                style={f.url ? undefined : { backgroundImage: artGradient(f.hue) }}
              >
                {f.url && <MediaImage src={f.url} alt="" className="h-full w-full object-cover" />}
              </button>
            ))}
          </div>
        )}

        {/* ── 2. CONTENT BODY ── */}
        <div className="space-y-4 p-5 sm:p-6 pt-2">
          
          {/* Title & Price */}
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold leading-tight text-white">{item.title}</h2>
              {(item.price !== null && item.price !== undefined) && (
                <p className="text-xl font-bold text-purple-300">
                  R{Number(item.price).toFixed(0)}
                  {item.priceNote && <span className="ml-1.5 text-xs font-normal text-zinc-400">{item.priceNote}</span>}
                </p>
              )}
            </div>
          </div>

          {/* Author Identity Block (Clickable to open ProfilePreviewDialog) */}
          {item.author && (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-zinc-800/70 bg-zinc-900/60 p-3.5 hover:border-purple-600/40 transition-all">
              <AuthorChip
                author={item.author}
                onClick={onAuthorClick ? () => onAuthorClick(item.author!) : undefined}
              />
              <FollowButton targetId={item.followTargetId ?? item.author.id} sample={item.sample} />
            </div>
          )}

          {/* Description */}
          {item.description && (
            <p className="text-sm leading-relaxed text-zinc-300 bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-3.5">
              {item.description}
            </p>
          )}

          {/* Vibe Tags */}
          {!!item.tags?.length && (
            <div className="flex flex-wrap gap-1.5">
              {item.tags.map((t) => (
                <Badge key={t} className="bg-purple-500/10 border border-purple-700/40 text-purple-300 text-xs px-2.5 py-1">
                  #{t}
                </Badge>
              ))}
            </div>
          )}

          {/* Fact Chips */}
          {!!item.facts?.length && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {item.facts.map((f) => (
                <div key={f.label} className="rounded-xl border border-zinc-800/60 bg-zinc-900/50 p-3 hover:border-zinc-700 transition-colors">
                  <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400 font-medium">
                    {f.icon && <f.icon className="w-3 h-3 text-purple-400" />}{f.label}
                  </p>
                  <p className="text-sm font-bold text-white truncate mt-1">{f.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Extra custom content */}
          {item.extra}

          {/* Action Ribbon */}
          {!!actions.length && (
            <div className="flex flex-wrap gap-2 pt-1">
              {actions.map((a) => (
                <Button
                  key={a.label}
                  variant={a.variant ?? "default"}
                  disabled={a.disabled}
                  onClick={a.onClick}
                  className={`gap-2 rounded-xl font-semibold active:scale-95 ${
                    a.variant === "default"
                      ? "bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 shadow-lg shadow-purple-900/30 text-white"
                      : "border-zinc-700 hover:bg-zinc-800 text-white"
                  }`}
                >
                  {a.icon && <a.icon className="w-4 h-4" />}{a.label}
                </Button>
              ))}
            </div>
          )}

          {/* Related Items */}
          {!!item.related?.length && (
            <div className="space-y-3 pt-1">
              <Separator className="bg-zinc-800/60" />
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{item.relatedTitle ?? "More Like This"}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {item.related.map((r) => (
                  <div key={r.id} className="group overflow-hidden rounded-xl border border-zinc-800/60 bg-zinc-900/50 hover:border-zinc-700 transition-all duration-200 hover:scale-[1.02] cursor-pointer">
                    {r.image ? (
                      <MediaImage src={r.image} alt={r.title} className="h-20 w-full object-cover group-hover:opacity-90 transition-opacity" loading="lazy" />
                    ) : (
                      <div className="h-20 w-full" style={{ backgroundImage: artGradient(r.hue ?? hueFromId(r.id)) }} />
                    )}
                    <div className="p-2.5">
                      <p className="text-xs font-semibold truncate text-white">{r.title}</p>
                      {r.meta && <p className="text-[10px] text-zinc-500 truncate mt-0.5">{r.meta}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ItemDetailDialog;
